import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Check, X, Loader2 } from "lucide-react";
import AuthLayout, { formatApiError } from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get("returnTo") || "/grove";
  const { signupWithPassword, login: googleLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [availability, setAvailability] = useState(null); // null | {available, reason?}
  const [checking, setChecking] = useState(false);

  // Live username availability — debounced by 400ms so we don't hit the API
  // on every keystroke. Skips when input is empty or too short.
  useEffect(() => {
    if (!username || username.length < 2) {
      setAvailability(null);
      setChecking(false);
      return;
    }
    setChecking(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/username-available?u=${encodeURIComponent(username)}`);
        setAvailability(data);
      } catch {
        setAvailability(null);
      } finally {
        setChecking(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [username]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (availability && !availability.available) {
      setError(availability.reason || "Please pick a different username.");
      return;
    }
    setSubmitting(true);
    try {
      await signupWithPassword({ email: email.trim(), password, username: username.trim() });
      toast.success("Account created — check your inbox for the verification link.");
      navigate(returnTo);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const pwOk = password.length >= 8;
  // Block submit for a handle we already know is unavailable so the user
  // isn't punished for a network round-trip they've already seen.
  const submitBlocked = availability !== null && !availability.available;
  const availStatus = checking
    ? { icon: <Loader2 className="w-4 h-4 animate-spin text-[#B79256]" />, text: "checking…", tone: "muted" }
    : availability?.available
      ? { icon: <Check className="w-4 h-4 text-[#4E5A3D]" />, text: "available", tone: "ok" }
      : availability
        ? { icon: <X className="w-4 h-4 text-[#8a3a2a]" />, text: availability.reason, tone: "err" }
        : null;

  return (
    <AuthLayout
      title="Create your account."
      subtitle="Plant seeds, save Field Notes, and keep gift lists in one honest corner of the internet."
      footer={
        <>
          Already have an account?{" "}
          <Link to="/login" data-testid="signup-to-login-link" className="text-[#4E5A3D] underline underline-offset-2">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#2A2823]/60" htmlFor="signup-username">Username</label>
          <input
            id="signup-username"
            data-testid="signup-username-input"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="kelly.compton"
            autoComplete="username"
            className="mt-1 w-full rounded-full border border-[#B79256]/40 bg-white px-4 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#4E5A3D]/30"
            required
            maxLength={30}
          />
          {availStatus && (
            <div
              data-testid="signup-username-status"
              className={`mt-1.5 flex items-center gap-1.5 text-xs ${
                availStatus.tone === "ok" ? "text-[#4E5A3D]" : availStatus.tone === "err" ? "text-[#8a3a2a]" : "text-[#2A2823]/60"
              }`}
            >
              {availStatus.icon}
              <span>{availStatus.text}</span>
            </div>
          )}
          <p className="mt-1 text-[11px] text-[#2A2823]/55">
            2–30 characters. Letters, numbers, underscore (_), period (.), or dash (-).
          </p>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#2A2823]/60" htmlFor="signup-email">Email</label>
          <input
            id="signup-email"
            data-testid="signup-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className="mt-1 w-full rounded-full border border-[#B79256]/40 bg-white px-4 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#4E5A3D]/30"
            required
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#2A2823]/60" htmlFor="signup-password">Password</label>
          <div className="mt-1 relative">
            <input
              id="signup-password"
              data-testid="signup-password-input"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="at least 8 characters"
              autoComplete="new-password"
              className="w-full rounded-full border border-[#B79256]/40 bg-white px-4 py-2.5 pr-11 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#4E5A3D]/30"
              required
              minLength={8}
            />
            <button
              type="button"
              data-testid="signup-toggle-password"
              onClick={() => setShowPw((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2A2823]/50 hover:text-[#2A2823]"
              aria-label={showPw ? "Hide password" : "Show password"}
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {password.length > 0 && (
            <div className={`mt-1.5 flex items-center gap-1.5 text-xs ${pwOk ? "text-[#4E5A3D]" : "text-[#2A2823]/60"}`}>
              {pwOk ? <Check className="w-4 h-4" /> : <div className="w-4 h-4" />}
              <span>{pwOk ? "Password looks good." : `${password.length}/8 minimum`}</span>
            </div>
          )}
        </div>

        {error && (
          <div
            data-testid="signup-error"
            className="text-sm text-[#8a3a2a] bg-[#8a3a2a]/10 border border-[#8a3a2a]/25 rounded-2xl px-4 py-2.5"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          data-testid="signup-submit"
          disabled={submitting || !email || !password || !username || submitBlocked}
          className="w-full rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-12 text-base shadow-none disabled:opacity-60"
        >
          {submitting ? "Creating your account…" : "Create account"}
        </Button>

        <div className="text-[11px] text-[#2A2823]/55 text-center leading-relaxed">
          By creating an account you agree to our{" "}
          <Link to="/terms" className="underline">Terms</Link> and{" "}
          <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </div>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs text-[#2A2823]/50">
        <div className="flex-1 h-px bg-[#B79256]/25" />
        <span>or</span>
        <div className="flex-1 h-px bg-[#B79256]/25" />
      </div>

      <Button
        type="button"
        onClick={googleLogin}
        data-testid="signup-google"
        variant="outline"
        className="w-full rounded-full h-12 border-[#B79256]/60 text-[#2A2823] hover:bg-[#B79256]/10 hover:text-[#2A2823] bg-transparent"
      >
        Continue with Google
      </Button>
    </AuthLayout>
  );
}
